import LZString from "lz-string";
import { shallowRef } from "vue";
import {
  ALL_LABELS,
  bundle_key,
  FALLBACK_PRICES,
  MATERIALS,
  NUM_BANDS,
  SPECIAL_LEAP_LABELS,
} from "@/Utils/Constants";
import { parse_locale_int } from "@/Utils/InputColumn";
import { debounce } from "@/Utils/Helpers";
import { RosterConfig } from "./RosterConfig";
import { from_saved, SavedColumn, SavedConfig, to_saved } from "./SavedConfig";

export const STORAGE_VERSION = 10;
export const STORAGE_KEY = "HF_CONFIG_V10_COMPRESSED";

// Older keys, newest first. On a version bump, move the current key to the top of
// this list. `version` is only a fallback for pre-V8 saves, which carry no version
// inside the payload.
const LEGACY_KEYS: { key: string; version: number }[] = [
  { key: "HF_CONFIG_V9_COMPRESSED", version: 9 },
  { key: "HF_CONFIG_V8_COMPRESSED", version: 8 },
  { key: "HF_CONFIG_V7_COMPRESSED", version: 7 },
  { key: "HF_CONFIG_V6_COMPRESSED", version: 6 },
  { key: "HF_UI_STATE_V5_COMPRESSED", version: 5 },
];

// V3 and V4 saves are no longer migrated. Removed so they stop taking up quota.
const ABANDONED_KEYS = [
  "HF_UI_STATE_V3_char_profiles",
  "HF_UI_STATE_V3_roster",
  "HF_UI_STATE_V4_roster",
];

const MIGRATIONS: Record<number, (data: any) => any> = {
  5: migrate_5_to_6,
  6: migrate_6_to_7,
  7: migrate_7_to_8,
  8: migrate_8_to_9,
  9: migrate_9_to_10,
};

// ============================================================================
// Load
// ============================================================================

// A save that could not be read, until the user downloads or discards it. It is kept under
// `<key>_BROKEN`; when that does not fit, only here, and saving is paused so the next write does
// not overwrite the original. Never loaded back by itself: the user imports the download.
export interface BrokenSave {
  key: string;
  version: number;
  raw: string;
  kept: boolean;
}
export const broken_save = shallowRef<BrokenSave | null>(null);

const ALL_KEYS = [
  { key: STORAGE_KEY, version: STORAGE_VERSION },
  ...LEGACY_KEYS,
];

function keep_broken(key: string, raw: string) {
  const version =
    ALL_KEYS.find((k) => k.key === key)?.version ?? STORAGE_VERSION;
  try {
    localStorage.setItem(`${key}_BROKEN`, raw);
    broken_save.value = { key, version, raw, kept: true };
  } catch {
    broken_save.value = { key, version, raw, kept: false };
  }
}

// one set aside by an earlier load
function find_kept_broken() {
  for (const { key, version } of ALL_KEYS) {
    const raw = localStorage.getItem(`${key}_BROKEN`);
    if (raw !== null) return { key, version, raw, kept: true };
  }
  return null;
}

// what the download holds: the save as a backup Import reads, or the raw string if unreadable
export function broken_backup(broken: BrokenSave): string {
  try {
    const data = JSON.parse(LZString.decompressFromUTF16(broken.raw));
    if (data === null || typeof data !== "object") throw new Error();
    return JSON.stringify({ version: broken.version, ...data }, null, 2);
  } catch {
    return broken.raw;
  }
}

export function discard_broken(state: { roster_config: RosterConfig }) {
  const paused = broken_save.value?.kept === false;
  for (const { key } of ALL_KEYS) localStorage.removeItem(`${key}_BROKEN`);
  broken_save.value = null;
  if (paused) write_state(state);
}

function load_compressed(key: string): any {
  const compressed = localStorage.getItem(key);
  if (compressed === null) {
    return null;
  }
  try {
    const data = JSON.parse(LZString.decompressFromUTF16(compressed));
    // garbage can decompress to null rather than throw
    if (data === null || typeof data !== "object") throw new Error();
    return data;
  } catch {
    keep_broken(key, compressed);
    return null;
  }
}

// The newest readable save wins and every legacy key is then deleted.
function read_newest_save(): {
  data: any;
  version: number;
  key: string;
  raw: string;
} | null {
  for (const { key, version } of ALL_KEYS) {
    const data = load_compressed(key);
    if (data === null) continue;
    const raw = localStorage.getItem(key);
    for (const legacy of LEGACY_KEYS) localStorage.removeItem(legacy.key);
    return { data, version: Number(data.version) || version, key, raw };
  }
  return null;
}

function migrate_to_current(data: any, version: number): any {
  let out = data;
  let v = version;
  while (v < STORAGE_VERSION) {
    out = MIGRATIONS[v](out);
    v += 1;
  }
  return out;
}

export function load_roster_config(): RosterConfig {
  for (const key of ABANDONED_KEYS) {
    localStorage.removeItem(key);
  }
  broken_save.value = find_kept_broken();
  const found = read_newest_save();
  if (found === null) {
    return from_saved({});
  }
  try {
    return from_saved(migrate_to_current(found.data, found.version));
  } catch (e) {
    console.error("could not load saved config, starting fresh", e);
    keep_broken(found.key, found.raw);
    return from_saved({});
  }
}

// ============================================================================
// Save
// ============================================================================

function serialize(config: RosterConfig): string {
  return JSON.stringify({ version: STORAGE_VERSION, ...to_saved(config) });
}

export function write_state(state: { roster_config: RosterConfig }) {
  if (broken_save.value?.kept === false) return;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      LZString.compressToUTF16(serialize(state.roster_config)),
    );
  } catch (e) {
    console.error("could not save config", e);
  }
}

export const debounced_write_roster_config = debounce(write_state, 500);

// ============================================================================
// Export / import
// ============================================================================

export function export_config(config: RosterConfig): string {
  return JSON.stringify(
    { version: STORAGE_VERSION, ...to_saved(config) },
    null,
    2,
  );
}

// Runs the same migration chain as a load, so older backups still import.
export function import_config(text: string): RosterConfig {
  const data = JSON.parse(text);
  const version = Number(data.version) || STORAGE_VERSION;
  if (version > STORAGE_VERSION) {
    throw new Error(
      `backup is from a newer version (${version}) than this site understands (${STORAGE_VERSION})`,
    );
  }
  return from_saved(migrate_to_current(data, version));
}

// ============================================================================
// Migrations
//
// 5 -> 6 and 6 -> 7 operate on the old shape, where the whole store was serialised
// as-is and material values were positional. 7 -> 8 converts to the label-keyed DTO,
// after which reordering material rows stops being a migration at all.
// ============================================================================

function migrate_5_to_6(data: any): any {
  delete data.selected_shard_bag_size; // replaced by shard_infos
  return data;
}

function migrate_6_to_7(data: any): any {
  // Material rows were reordered: juices went from grouped-by-slot to interleaved.
  const swap_key_map = [
    [
      0, 1, 2, 3, 4, 5, 6, 15, 7, 16, 8, 17, 9, 18, 10, 19, 11, 20, 12, 21, 13,
      22, 14,
    ],
    [0, 1, 2, 3, 4, 5, 6, 8, 7],
  ];

  function swap(columns: any[]): any[] {
    if (!Array.isArray(columns)) {
      return columns;
    }
    for (const [tier, column] of columns.entries()) {
      const map = swap_key_map[tier];
      if (!map || !column) {
        continue;
      }
      const copy = structuredClone(column);
      for (const [row, source] of map.entries()) {
        for (const field of ["data", "keys", "upper_bound", "enabled"]) {
          if (Array.isArray(column[field])) {
            column[field][row] = copy[field][source];
          }
        }
      }
    }
    return columns;
  }

  for (const key in data.mats_prices ?? {}) {
    data.mats_prices[key] = swap(data.mats_prices[key]);
  }
  for (const key in data.roster_mats_owned ?? {}) {
    data.roster_mats_owned[key] = swap(data.roster_mats_owned[key]);
    data.tradable_mats_owned[key] = swap(data.tradable_mats_owned[key]);
  }
  for (const profile of data.profiles ?? []) {
    // the vambrace row did not exist before V7
    if (Array.isArray(profile.normal_grid) && profile.normal_grid.length < 7) {
      profile.normal_grid.push(Array(25).fill(2 /* NotYet */));
    }
    profile.bound_budgets = swap(profile.bound_budgets);
    profile.leftover_price = swap(profile.leftover_price);
  }
  return data;
}

// V7 stored one column per tier, with values positional against that tier's label list,
// so a material shared between tiers appeared twice. Prices and owned materials were kept
// in sync by a watcher; a character's bound budget was not, so `winner` says which tier's
// copy survives where the two disagree.
//
// Deliberately ignores the old column's own `keys` array. Nothing before V8 ever read it,
// so it drifted out of order in real saves, while `data` was always interpreted
// positionally against ALL_LABELS. Position is the only trustworthy mapping here.
function v7_merge_columns(
  columns: any,
  winner: number,
  key_of: (label: string) => string = (label) => label,
): SavedColumn {
  const values: Record<string, number> = {};
  const disabled = new Set<string>();
  const tiers = ALL_LABELS.map((_, tier) => tier).filter((t) => t !== winner);
  tiers.push(winner); // the winning tier is applied last, so it overwrites
  for (const tier of tiers) {
    const old = columns?.[tier];
    ALL_LABELS[tier].forEach((label, row) => {
      // a row added after the save was made is left out, so from_saved uses the template
      if (old?.data?.[row] === undefined) return;
      const parsed = parse_locale_int(String(old.data[row]));
      values[key_of(label)] = Number.isFinite(parsed) ? parsed : 0;
      if (old?.enabled?.[row] === false) {
        disabled.add(key_of(label));
      } else {
        disabled.delete(key_of(label));
      }
    });
  }
  return disabled.size > 0 ? { values, disabled: [...disabled] } : { values };
}

function migrate_7_to_8(data: any): Partial<SavedConfig> {
  // V7 stored one price per material; prices are now per bundle, so each material's
  // single bundle size supplies the key.
  const price_key = (label: string) =>
    bundle_key(label, MATERIALS[label].bundle_sizes[0]);

  const mats_prices: SavedConfig["mats_prices"] = {};
  const selected_bundles: SavedConfig["selected_bundles"] = {};
  for (const region in data.mats_prices ?? {}) {
    const column = v7_merge_columns(data.mats_prices[region], 0, price_key);
    // shard bags were a separate structure with their own prices and chosen size
    const old_shards = data.shard_infos?.[region];
    for (const size in old_shards?.prices ?? {}) {
      column.values[bundle_key("Shards", Number(size))] = parse_locale_int(
        String(old_shards.prices[size]?.data?.[0] ?? "0"),
      );
    }
    mats_prices[region] = column;
    selected_bundles[region] = { Shards: Number(old_shards?.selected) || 3000 };
  }

  const roster_mats_owned: SavedConfig["roster_mats_owned"] = {};
  const tradable_mats_owned: SavedConfig["tradable_mats_owned"] = {};
  for (const roster_id in data.roster_mats_owned ?? {}) {
    roster_mats_owned[roster_id] = v7_merge_columns(
      data.roster_mats_owned[roster_id],
      0,
    );
    tradable_mats_owned[roster_id] = v7_merge_columns(
      data.tradable_mats_owned?.[roster_id],
      0,
    );
  }

  const profiles = (data.profiles ?? []).map((profile: any) => {
    const keyed_upgrades: Record<string, any> = {};
    for (const [key, upgrade] of Object.entries(profile.keyed_upgrades ?? {})) {
      if (upgrade !== null && typeof upgrade === "object") {
        const { used_materials: _drop, ...rest } = upgrade as any;
        keyed_upgrades[key] = rest;
      }
    }
    const tier = profile.tier === 1 ? 1 : 0;
    // the single V7 special-leap value is in the character's current tier's units
    const special_leaps = parse_locale_int(
      String(profile.special_budget?.data?.[0] ?? "0"),
    );
    return {
      char_name: profile.char_name,
      roster_id: profile.roster_id,
      tier,
      express_event: profile.express_event,
      auto_start_optimizer: profile.auto_start_optimizer,
      min_resolution: 10,
      lock_fetched_done: profile.lock_fetched_done,
      pretend_30_40_x2_grace: profile.pretend_30_40_x2_grace,
      // V7's optimizer_treatment_plan is dropped rather than mapped: band_values is left
      // out here so profile_from_saved supplies the default. chance_band is a different
      // setting (which ownership line the chance column reads) and does carry over.
      chance_band: Math.min(
        Math.max(Number(profile.histogram_treatment_plan) || 0, 0),
        NUM_BANDS - 1,
      ),
      normal_grid: profile.normal_grid,
      adv_grid: profile.adv_grid,
      keyed_upgrades,
      special_budget: {
        values: {
          [SPECIAL_LEAP_LABELS[tier]]: Number.isFinite(special_leaps)
            ? special_leaps
            : 0,
        },
      },
      bound_budgets: v7_merge_columns(profile.bound_budgets, tier),
      // V7 had no chests
      chests: [],
    };
  });

  return {
    version: 8,
    profiles,
    active_profile_index: data.active_profile_index,
    last_seen_version: data.last_seen_version,
    mats_prices,
    roster_mats_owned,
    tradable_mats_owned,
    all_regions: data.all_regions,
    selected_bundles,
    latest_market_data: data.latest_market_data,
    cumulative_graph: false,
    show_all_rows: data.show_all_rows,
    auto_deduct_costs: data.auto_deduct_costs,
    auto_fetch: data.auto_fetch,
  };
}

// The V7 migration wrote 0 for books a V7 save did not have yet, overriding the fallback.
// A 0 is reset to the fallback; any other value was the user's.
const V8_ZEROED_BOOKS = [
  "Enhanced 19-20 Armor",
  "Enhanced 19-20 Weapon",
  "12-15 Armor",
  "12-15 Weapon",
  "16-19 Armor",
  "16-19 Weapon",
];

function migrate_8_to_9(data: any): any {
  for (const column of Object.values<any>(data.mats_prices ?? {})) {
    for (const label of V8_ZEROED_BOOKS) {
      const key = bundle_key(label, MATERIALS[label].bundle_sizes[0]);
      if (column.values?.[key] === 0) column.values[key] = FALLBACK_PRICES[key];
    }
  }
  data.version = 9;
  return data;
}

// Roster ids become uuids. Profiles are sorted by the old id first, so the rosters keep their order.
function migrate_9_to_10(data: any): any {
  const ids: Record<string, string> = {};
  const id_of = (old: any) => (ids[String(old)] ??= crypto.randomUUID());
  const rekey = (record: any) =>
    Object.fromEntries(
      Object.entries(record ?? {}).map(([old, value]) => [id_of(old), value]),
    );
  const profiles: any[] = data.profiles ?? [];
  const active = profiles[data.active_profile_index ?? 0];
  data.profiles = profiles.toSorted(
    (a, b) => (Number(a.roster_id) || 0) - (Number(b.roster_id) || 0),
  );
  data.active_profile_index = Math.max(0, data.profiles.indexOf(active));
  for (const item of [...data.profiles, ...(data.scan_pages ?? []), ...(data.snapshots ?? [])]) {
    item.roster_id = id_of(item.roster_id);
  }
  for (const key of [
    "roster_mats_owned",
    "tradable_mats_owned",
    "roster_chests",
    "roster_income",
    "custom_choices",
    "all_regions",
  ]) {
    data[key] = rekey(data[key]);
  }
  data.version = 10;
  return data;
}
